import { useEffect, useState, useCallback } from 'react'
import { apiGet, apiPost } from '../api'
import type { FocusResponse } from '../api'

export default function FocusPanel() {
  const [data, setData] = useState<FocusResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setErr(null)
      const res = await apiGet<FocusResponse>('/focus')
      setData(res)
    } catch (e) {
      setErr(String(e))
    }
  }, [])

  const regen = useCallback(async () => {
    setLoading(true)
    try {
      const res = await apiPost<FocusResponse>('/focus/regenerate')
      setData(res)
    } catch (e) {
      setErr(String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
    const t = setInterval(load, 5 * 60_000)
    return () => clearInterval(t)
  }, [load])

  return (
    <section className="panel focus work-panel">
      <div className="panel-header">
        <span>Tonight's focus</span>
        <button type="button" onClick={regen} disabled={loading} aria-label="Regenerate focus" style={{ minHeight: 32, padding: '0.25rem 0.6rem' }}>
          {loading ? '…' : '↻'} regenerate
        </button>
      </div>
      {err && <div className="empty-state">Couldn't load focus: {err}</div>}
      {!data && !err && <div className="empty-state">Loading…</div>}
      {data && data.top.length === 0 && !data.error && (
        <div className="empty-state">Nothing to focus on. Either you're caught up or the agent isn't reporting in.</div>
      )}
      {data && data.error && (
        <div className="empty-state" style={{ color: 'var(--critical)', fontFamily: 'var(--font-mono)', fontSize: '0.72rem' }}>
          AI returned no structured picks — {data.error}
        </div>
      )}
      {data && data.generated_at > 0 && (
        <div style={{ fontSize: '0.62rem', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)',
                      letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 6 }}>
          last picked {new Date(data.generated_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          {data.debounced && ' · debounced (try in 30s)'}
          {data.model && ' · ' + data.model.replace('@cf/', '').slice(0, 32)}
        </div>
      )}
      {data && data.top.length > 0 && (
        <ol className="focus-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {data.top.map((item, i) => (
            <li key={item.id} className="focus-item">
              <div className="focus-rank">{i + 1}.</div>
              <div>
                <div className="focus-title">{item.title}</div>
                <div className="focus-reason">{item.reason}</div>
                <span className={`pill ${item.source === 'session' ? 'amber' : item.source === 'task' ? 'warm' : ''}`} style={{ marginTop: '0.35rem' }}>{item.source}</span>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}
